import { useCallback, useEffect, useRef, useState } from "react"
import { router } from "@inertiajs/react"
import { Loader2, Send, Sparkles, Eraser, X as IconX } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Sheet } from "@/components/ui/sheet"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { ChatMessageBubble } from "@/components/domain/chat-message-bubble"
import { ChatThinkingIndicator } from "@/components/domain/chat-thinking-indicator"
import {
  PolicyProposalDiffCard,
  type PolicyProposal,
} from "@/components/domain/policy-proposal-diff-card"
import { consumeSSE } from "@/lib/sse"
import { cn } from "@/lib/cn"
import type { ChatBubble } from "@/types/test_lab"
import type {
  CustomFieldCatalog,
  EscalationPolicy,
  PolicyAgentOption,
  PolicyUserOption,
  AgentPoolOption,
  PolicyReasonOption,
  PolicyTopicOption,
} from "@/types/escalation_policy"

// Forge co-edit modal for /escalation_policies. Uses the same chat-bubble
// + diff-card primitives as the skills DesignerPane so the two surfaces
// look and feel identical. Endpoints:
//   POST   /escalation_policies/ai_chat            (SSE; text + tool_proposal)
//   PATCH  /escalation_policies/apply_proposals    (atomic apply against draft)
//   GET    /escalation_policies/designer_chat      (history; v1: empty list)
//   DELETE /escalation_policies/designer_chat      (clear thread)

type Message = {
  id:        number
  role:      "user" | "assistant"
  content:   string
  createdAt: string
}

type Props = {
  /** Closes the modal. */
  onClose: () => void
  /** True when an editable draft set already exists. False = Apply will
   *  auto-clone live → draft on the server (Portal::EscalationPolicies::
   *  ApplyProposals#ensure_draft_set!). FE shows a heads-up banner. */
  isDraft: boolean
  /** Persisted policies for the active set — diff cards render `before`
   *  by id lookup. Pass an empty list when no policies exist yet. */
  policies: EscalationPolicy[]
  /** FK lookups so the diff card can render names instead of ids. */
  users:         PolicyUserOption[]
  agents:        PolicyAgentOption[]
  agentPools:    AgentPoolOption[]
  reasonOptions: PolicyReasonOption[]
  topicOptions:  PolicyTopicOption[]
  /** Live custom-field catalog so predicate diffs render field labels
   *  + choice colors instead of crashing on undefined lookups. */
  customFieldCatalog: CustomFieldCatalog
}

export function EscalationPolicyDesignerPane({
  onClose, isDraft, policies, users, agents, agentPools, reasonOptions, topicOptions, customFieldCatalog,
}: Props) {
  const [messages, setMessages]     = useState<Message[]>([])
  const [draft, setDraft]           = useState("")
  const [streaming, setStreaming]   = useState(false)
  const [proposals, setProposals]   = useState<PolicyProposal[]>([])
  const [applyError, setApplyError] = useState<string | null>(null)
  const [applying, setApplying]     = useState(false)
  const threadRef = useRef<HTMLDivElement | null>(null)

  // Replay persisted thread on mount (v1: server returns empty list — chat is
  // client-side only — but we wire the fetch so the server can flip it on
  // later without a FE change).
  useEffect(() => {
    fetch(`/escalation_policies/designer_chat`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        const rows = (data?.messages ?? []) as Array<{ id: number; role: string; content: string | null; createdAt?: string; toolProposal?: unknown }>
        const turns: Message[] = []
        const queued: PolicyProposal[] = []
        for (const m of rows) {
          if (m.role === "user" || m.role === "assistant") {
            turns.push({ id: m.id, role: m.role, content: m.content ?? "", createdAt: m.createdAt ?? new Date().toISOString() })
          } else if (m.role === "tool_proposal" && m.toolProposal) {
            const tp = m.toolProposal as { id?: string; kind?: string; input?: Record<string, unknown> }
            queued.push({
              id:       tp.id ?? `prop-${m.id}`,
              kind:     tp.kind ?? "unknown",
              input:    tp.input ?? {},
              accepted: true,
            })
          }
        }
        setMessages(turns)
        setProposals(queued)
      })
      .catch(() => {/* leave empty; user can still chat */})
  }, [])

  // Auto-scroll thread on new content.
  useEffect(() => {
    threadRef.current?.scrollTo({ top: threadRef.current.scrollHeight, behavior: "smooth" })
  }, [messages.length, streaming])

  const streamReply = useCallback(async (history: Message[]) => {
    setStreaming(true)
    await consumeSSE(
      `/escalation_policies/ai_chat`,
      {
        method: "POST",
        body: new URLSearchParams({ messages: JSON.stringify(history) }),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      },
      {
        onText: (delta) => {
          setMessages((cur) => {
            const next = [...cur]
            const last = next[next.length - 1]
            if (last && last.role === "assistant") next[next.length - 1] = { ...last, content: last.content + delta }
            return next
          })
        },
        onToolProposal: (raw) => {
          const p = raw as { id?: string; kind?: string; input?: Record<string, unknown>; number?: number }
          setProposals((cur) => [
            ...cur,
            {
              id:       p.id ?? `prop-${Date.now()}-${cur.length}`,
              kind:     p.kind ?? "unknown",
              input:    p.input ?? {},
              accepted: true,
              number:   p.number,
            },
          ])
        },
        onError: (msg) => {
          setMessages((cur) => [
            ...cur,
            { id: Date.now(), role: "assistant", content: `(error: ${msg})`, createdAt: new Date().toISOString() },
          ])
        },
        onEnd: () => setStreaming(false),
      },
    )
    setStreaming(false)
  }, [])

  const send = useCallback(async () => {
    const text = draft.trim()
    if (streaming || !text) return
    setDraft("")
    const now = new Date().toISOString()
    const newHistory: Message[] = [
      ...messages,
      { id: Date.now(),     role: "user",      content: text, createdAt: now },
      { id: Date.now() + 1, role: "assistant", content: "",   createdAt: now },
    ]
    setMessages(newHistory)
    await streamReply(newHistory.slice(0, -1))
  }, [draft, messages, streaming, streamReply])

  function toggleProposal(id: string) {
    setProposals((cur) => cur.map((p) => (p.id === id ? { ...p, accepted: !p.accepted } : p)))
  }

  function removeProposal(id: string) {
    setProposals((cur) => cur.filter((p) => p.id !== id))
  }

  async function applySelected() {
    const accepted = proposals.filter((p) => p.accepted)
    if (accepted.length === 0) return
    setApplying(true)
    setApplyError(null)
    try {
      const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
      const res = await fetch(`/escalation_policies/apply_proposals`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token":  token ?? "",
          "Content-Type":  "application/json",
          "Accept":        "application/json",
        },
        body: JSON.stringify({
          // Server expects nested `input` (Portal::EscalationPolicies::
          // ApplyProposals#dispatch reads p["input"]). Don't spread the
          // input keys to the top level — the policy id (input.id) and
          // proposal id (top-level id) are different and the dispatch
          // can't disambiguate.
          proposals: accepted.map(({ id, kind, input }) => ({ id, kind, input })),
        }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        setApplyError(body.error ?? `HTTP ${res.status}`)
        return
      }
      const appliedIds = new Set(accepted.map((p) => p.id))
      setProposals((cur) => cur.filter((p) => !appliedIds.has(p.id)))
      // Apply succeeded — close the modal so the operator lands back on
      // the policy list and sees the new draft state immediately. Use a
      // partial reload that explicitly names every prop the apply can
      // mutate; bare router.reload() defaults to preserveState:true and
      // can leave ticketQueues / agentPools stale when Forge mints a new
      // queue or pool inline (e.g. send_to_queue with assignee_ticket_queue_name).
      onClose()
      router.reload({
        only: ["activeSet", "liveSet", "draftSet", "lineage", "policies", "diff", "ticketQueues", "agentPools"],
      })
    } finally {
      setApplying(false)
    }
  }

  async function clearChat() {
    if ((messages.length > 0 || proposals.length > 0) &&
        !confirm("Clear the chat with Forge and any pending proposals?")) return
    try {
      const token = document.querySelector<HTMLMetaElement>("meta[name=csrf-token]")?.content
      await fetch(`/escalation_policies/designer_chat`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": token ?? "" },
      })
    } catch { /* clear locally regardless */ }
    setMessages([])
    setProposals([])
    setApplyError(null)
  }

  const acceptedCount = proposals.filter((p) => p.accepted).length
  const lookups = { policies, users, agents, agentPools, reasonOptions, topicOptions, customFieldCatalog }

  return (
    // Bottom sheet on mobile, centered modal on desktop (capped at
    // ~1152px = `lg:max-w-6xl`). The pane has its own header inside
    // the body so we don't use SheetHeader/Footer here.
    <Sheet open onClose={onClose} side="bottom" width="lg:max-w-6xl" className="bg-app-bg">
      <div className="flex h-[80vh] max-h-[80vh] flex-col gap-3 p-4 lg:h-[60vh] lg:max-h-[60vh]">
        <header className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-vip-goldSoft" />
            <h2 className="text-lg font-semibold">Design with Forge</h2>
            <InfoTooltip side="bottom">
              <div className="space-y-2">
                <div className="font-semibold text-app-text">Things you can ask Forge to do:</div>
                <ul className="list-disc space-y-1 pl-4">
                  <li>Add a new rule (for example, "Send VIP users to the senior team").</li>
                  <li>Update an existing rule. Change who gets it, the priority, or the match conditions.</li>
                  <li>Archive a rule you don't want anymore.</li>
                  <li>Reorder your rules so a more specific one runs before a catch-all.</li>
                  <li>Mark certain tickets as high or urgent priority.</li>
                  <li>Create a new queue (lane in your inbox) and send tickets there.</li>
                  <li>Catch repeat help-seekers — like users who escalate 3 or more times in a few days.</li>
                  <li>Route by tag, escalation reason, or conversation topic.</li>
                </ul>
                <div className="text-app-muted">
                  Forge writes everything as draft proposals on the right. Nothing changes until you click Apply.
                </div>
              </div>
            </InfoTooltip>
            {!isDraft && (
              <span className="rounded-control border border-vip-gold/30 bg-vip-goldMute/40 px-2 py-0.5 text-[10px] text-vip-goldSoft">
                New draft will be created on Apply
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-control p-1 text-app-muted hover:bg-white/5 hover:text-app-text"
            aria-label="Close"
          >
            <IconX size={18} />
          </button>
        </header>

        {/* Inner two-pane body — stacks vertically below lg: so the
            composer + proposals each get full width on phones. The
            outer Sheet already adapted via Chunk 2.5; this fixes the
            still-row inner body that was force-shrinking both halves
            below the readable threshold on narrow viewports. */}
        <div className="flex flex-1 min-h-0 flex-col gap-3 lg:flex-row">
          {/* Left: chat thread + composer */}
          <Card className="flex flex-1 min-w-0 min-h-0 flex-col p-0">
            <div ref={threadRef} className="flex-1 space-y-6 overflow-y-auto px-6 py-6 text-sm">
              {messages.length === 0 && (
                <p className="text-xs text-app-muted">
                  Tell Forge what you want — e.g. "VIP users always go to Pat,
                  billing topics get prioritized, repeat help-seekers (≥ 3 in 7d)
                  bump to high priority." Forge will draft a set of proposed
                  changes you can accept or reject on the right.
                </p>
              )}
              {messages.map((m, i) => {
                // Hide the empty assistant placeholder while we wait for the
                // first text delta — the thinking indicator covers that gap.
                const isStreamingPlaceholder = streaming && i === messages.length - 1 && m.role === "assistant" && m.content === ""
                if (isStreamingPlaceholder) return null
                const bubble: ChatBubble = {
                  id:         m.id,
                  role:       m.role,
                  content:    m.content,
                  createdAt:  m.createdAt,
                  authorName: m.role === "assistant" ? "Forge" : "You",
                }
                return (
                  <ChatMessageBubble
                    key={m.id}
                    msg={bubble}
                    userLabel="You"
                    assistantLabel="Forge"
                  />
                )
              })}
              {streaming && <ChatThinkingIndicator label="Forge" />}
            </div>

            <footer className="flex items-end gap-2 border-t border-border p-3">
              <Textarea
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault()
                    send()
                  }
                }}
                placeholder="Ask Forge…"
                rows={2}
                className="flex-1"
                disabled={streaming}
              />
              <Button
                type="button" variant="red" size="md"
                onClick={send}
                disabled={streaming || !draft.trim()}
                className="self-end"
                aria-label={streaming ? "Forge is thinking" : "Send"}
              >
                {streaming
                  ? <Loader2 size={16} strokeWidth={2.5} className="animate-spin" />
                  : <Send size={16} strokeWidth={2} />}
              </Button>
              <Button
                type="button" variant="ghost" size="md"
                onClick={clearChat}
                disabled={streaming || (messages.length === 0 && proposals.length === 0)}
                title="Clear chat + pending proposals"
              >
                <Eraser size={14} />
              </Button>
            </footer>
          </Card>

          {/* Right: pending changes (field-level diff cards) */}
          <Card className="flex flex-1 min-w-0 min-h-0 flex-col p-0">
            <header className="flex items-center justify-between border-b border-border px-4 py-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">Pending changes</span>
                {proposals.length > 0 && (
                  <span className="rounded-chip border border-vip-gold/30 bg-vip-goldMute px-2 py-0.5 text-[10px] text-vip-goldSoft">
                    {acceptedCount} / {proposals.length} accepted
                  </span>
                )}
              </div>
              <Button
                variant="red"
                size="sm"
                onClick={applySelected}
                disabled={applying || acceptedCount === 0}
              >
                {applying ? "Applying…" : `Apply ${acceptedCount}`}
              </Button>
            </header>
            <div className={cn(
              "flex-1 overflow-y-auto p-3",
              proposals.length === 0 ? "" : "space-y-2",
            )}>
              {proposals.length === 0 ? (
                <p className="text-xs text-app-muted">
                  Forge's proposed changes will appear here. Send a message on the left to get started.
                </p>
              ) : (
                proposals.map((p) => (
                  <PolicyProposalDiffCard
                    key={p.id}
                    proposal={p}
                    onToggle={toggleProposal}
                    onRemove={removeProposal}
                    lookups={lookups}
                  />
                ))
              )}
              {applyError && (
                <p className="rounded-control border border-status-error/30 bg-status-error/10 px-2 py-1 text-xs text-status-error">
                  {applyError}
                </p>
              )}
            </div>
          </Card>
        </div>
      </div>
    </Sheet>
  )
}
